import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { byLocation, fetchSpecimens } from "../api";
import { fetchPlan } from "../tasksApi";
import { LocationSection } from "../components/LocationSection";
import { LanguageToggle, useLanguage } from "../i18n";
import { EmptyState, ErrorState, LoadingState } from "../components/States";
import type { Specimen } from "../types";

type Load =
  | { status: "loading" }
  | { status: "ready"; specimens: Specimen[] }
  | { status: "failed"; message: string };

/**
 * A plant the guide cannot schedule where it is produces no Task at all, so on
 * Today it appears only in the wrong-place card. Here it would otherwise look
 * exactly like a plant that is simply not due — and absent is indistinguishable
 * from fine.
 */
function useUnschedulable(): Set<string> {
  const [ids, setIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const controller = new AbortController();
    fetchPlan(controller.signal)
      .then((plan) => setIds(new Set(plan.warnings.map((warning) => warning.specimen_id))))
      .catch(() => {
        // The collection still lists everything; it just cannot flag them.
      });
    return () => controller.abort();
  }, []);

  return ids;
}

export function CollectionPage() {
  const { t } = useLanguage();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const unschedulable = useUnschedulable();

  useEffect(() => {
    const controller = new AbortController();

    fetchSpecimens(controller.signal)
      .then((specimens) => setLoad({ status: "ready", specimens }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setLoad({ status: "failed", message: error instanceof Error ? error.message : String(error) });
      });

    return () => controller.abort();
  }, []);

  return (
    <main className="shell">
      <header className="masthead">
        <div>
          <h1>{t.allPlants}</h1>
          {load.status === "ready" && load.specimens.length > 0 && (
            <p className="masthead-sub">
              <span>
                {load.specimens.length} {t.plants}
              </span>
            </p>
          )}
        </div>
        <p className="masthead-nav">
          <Link className="nav-pill" to="/">
            {t.today}
          </Link>
          <LanguageToggle />
        </p>
      </header>

      {load.status === "loading" && <LoadingState />}
      {load.status === "failed" && <ErrorState message={load.message} />}
      {load.status === "ready" &&
        (load.specimens.length === 0 ? (
          <EmptyState />
        ) : (
          byLocation(load.specimens).map((group) => (
            <LocationSection
              key={group.location.id}
              location={group.location}
              specimens={group.specimens}
              unschedulable={unschedulable}
            />
          ))
        ))}
    </main>
  );
}
